import { useState, useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";
import Axios, { markPageUnloading } from '@/shared/api/axios';

import PageLayout from '@/components/layout/PageLayout';
import LeftSidebar from '@/components/layout/LeftSidebar';
import WorldViewSidebar from '@/features/worldView/components/WorldViewSidebar';
import PostCard from '@/features/posts/components/PostCard';
import StatusModal from '@/components/ui/StatusModal';

function WorldViewLayout() {
    const params = useParams();
    const navigate = useNavigate();

    const idolId = useSelector((state) => state.user.idolId);

    const [isLoading, setIsLoading] = useState(false);
    const [info, setInfo] = useState(null);

    // API 非成功狀態碼統一由 StatusModal 顯示（含攔截器附加的 HTTP_xxx）
    const statusModalRef = useRef(null);
    const [currentStatusCode, setCurrentStatusCode] = useState('');

    const getInfo = async () => {
        setIsLoading(true);
        try {
            const res = await Axios.post("/world/get-info", {
                idol_id: idolId,
                world_uuid: params.worldUuid
            });
            switch (res.data.status_code) {
                case 'SYSTEM_1000':
                    setInfo(res.data.data);
                    break;
                case 'SYSTEM_1001':
                    navigate('/404');
                    break;
                default:
                    setCurrentStatusCode(res.data.status_code);
                    statusModalRef.current?.show();
                    break;
            }
        } catch (error) {
            // NETWORK_ERROR 由全域 ServerErrorPage 處理，不另外顯示 modal
            if (error.httpErrorCode && error.httpErrorCode !== 'NETWORK_ERROR') {
                setCurrentStatusCode(error.httpErrorCode);
                statusModalRef.current?.show();
            } else if (!error.httpErrorCode) {
                console.error('Get world info error:', error);
            }
        } finally {
            setIsLoading(false);
        }
    };

    // 點擊 tags 導向首頁並顯示相關貼文和世界
    const updateListWithTag = (tag) => {
        markPageUnloading();
        window.location.href = `/?tags=${tag}`;
    };

    // 本地更新單一世界觀，取代整包 refetch（避免按讚/收藏/留言時畫面閃動）
    // updater 回傳 null/undefined 視為「移除」語意（例如刪除），單篇頁面沒有列表可移除，改用既有 getInfo() 重新請求
    // PostCard 傳入的 updater 都是純函式，且「是否回傳 null」與傳入的資料無關（所以 await 後舊閉包的 info 也能拿來試算），
    // 先試算一次判斷是否為「移除」。本頁不可傳 removeOnUncollect 給 PostCard：取消收藏成功後會送出移除（回傳 null 的 updater），會被當成刪除處理；
    // 不在 setState 的 updater 裡設旗標再同步讀取：那依賴 React 的 eager state 優化，不保證 updater 當下就執行
    const patchInfo = (updater) => {
        if (!info) return;
        if (!updater(info)) {
            getInfo();
            return;
        }
        setInfo(prev => (prev ? updater(prev) : prev));
    };

    useEffect(() => {
        getInfo();
    }, []);

    return (
        <PageLayout
            leftSidebar={<LeftSidebar />}
            rightSidebar={
                info ? (
                    <WorldViewSidebar
                        sideInfo={info.side_info}
                        worldId={info.world_info.world_id}
                        updateInfo={getInfo}
                    />
                ) : null
            }
            isLoading={isLoading}
        >
            {info ? (
                <PostCard
                    key={info.world_info.world_id}
                    data={info}
                    contentType="world"
                    showAuthorInfo={true}
                    showFullContent={true}
                    onItemChange={patchInfo}
                    updateList={getInfo}
                    onTagClick={updateListWithTag}
                />
            ) : ''}
            <StatusModal ref={statusModalRef} statusCode={currentStatusCode} />
        </PageLayout>
    );
}

// 以網址上的 uuid 當 key：站內從一篇切到另一篇（例如點通知）時重新掛載。
// 頁面只在掛載時抓資料，不重新掛載的話畫面會停在前一篇，前一篇的延遲計時器（刪除後導頁）也不會清掉
function WorldViewRoute() {
    const { worldUuid } = useParams();
    return <WorldViewLayout key={worldUuid} />;
}

export default WorldViewRoute;
